import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Send, CheckCircle2, ArrowLeft, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SiteHeader from '../components/SiteHeader';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Signed-in users contribute an Insider tip: paste a news link (and/or a
// headline/snippet). The backend runs the same LLM extractor the feeds use,
// shows what it found, and queues it for admin review before it goes live.
export default function InsiderSubmit() {
  const { token } = useAuth();
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { preview: {...} }

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    if (!url.trim() && !text.trim()) {
      setError('Paste a link or some text to submit.');
      return;
    }
    setSubmitting(true);
    try {
      const resp = await fetch(`${API_BASE}/api/insider/submissions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() || null, text: text.trim() || null }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setError(data.detail || 'Could not submit that — please try again.');
      } else {
        setResult(data);
        setUrl('');
        setText('');
      }
    } catch {
      setError('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const preview = result?.preview;

  return (
    <div className="min-h-screen bg-gray-50">
      <SiteHeader />
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/insider" className="inline-flex items-center text-videbits-700 hover:text-videbits-900 mb-6">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Insider
        </Link>

        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 mb-1">
          <Zap className="w-6 h-6 text-videbits-600" /> Submit a tip
        </h1>
        <p className="text-gray-600 mb-6">
          Spotted casting, filming, a release date, award or other project or
          people news? Paste the link and we'll extract the events, people and
          roles. An editor reviews every submission before it appears on Insider.
        </p>

        {result ? (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center gap-2 text-green-600 mb-3">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-semibold">Thanks — submitted for review.</span>
            </div>
            {preview && (
              <div className="border border-gray-100 rounded-lg p-4 bg-gray-50">
                <p className="text-sm text-gray-500 mb-1">We read this as:</p>
                <p className="font-medium text-gray-900">
                  {preview.project_title}
                  {preview.media_type ? (
                    <span className="ml-2 text-xs uppercase tracking-wide text-videbits-600">
                      {preview.media_type}
                    </span>
                  ) : null}
                </p>
                <ul className="mt-2 space-y-1">
                  {(preview.events || []).map((ev, i) => (
                    <li key={i} className="text-sm text-gray-700">
                      • {ev.event_type.replace(/_/g, ' ').toLowerCase()}
                      {ev.description ? ` — ${ev.description}` : ''}
                      {ev.metadata?.person && (
                        <span className="block ml-3 text-videbits-700">
                          {ev.metadata.person}
                          {ev.metadata.role ? ` · ${ev.metadata.role}` : ''}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button
              onClick={() => setResult(null)}
              className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-videbits-600 text-white rounded-lg font-medium hover:bg-videbits-700"
            >
              <Send className="w-4 h-4" /> Submit another
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">News link</label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://variety.com/2026/..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-videbits-500 focus:border-videbits-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Or paste the headline / a short summary
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={3}
                placeholder="e.g. Pedro Pascal wins Best Actor, or Dune: Part Three begins filming with Denis Villeneuve returning to direct."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-videbits-500 focus:border-videbits-500 outline-none"
              />
            </div>

            <div className="flex items-start gap-2 text-xs text-gray-500">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                Please don't paste full article text — a link plus the headline is
                perfect. We only store the headline, a short summary and the link.
              </span>
            </div>

            {error && (
              <div className="px-3 py-2 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-videbits-600 text-white rounded-lg font-medium hover:bg-videbits-700 disabled:opacity-50"
            >
              <Send className="w-4 h-4" /> {submitting ? 'Analysing…' : 'Submit for review'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
